/**
 * `useSyncExternalStore` bindings that make the state machine the SINGLE source
 * of truth for React consumers.
 *
 * This is the alternative to mirroring the machine's phase into react-sweet-state
 * (which the store currently does via `patchSnapshot`). Here, React subscribes to
 * the machine directly:
 *
 *   getSnapshot = () => machine.state()        // late-mount read + current value
 *   subscribe   = (cb) => subscribeToMachine() // re-render on transition
 *
 * Both the "I mounted after the flow started" case (initial `getSnapshot` read)
 * and the "flow changed while I'm mounted" case (subscription) are handled by the
 * one hook, with no duplicated phase state.
 *
 * The registry + bridge live outside React (module scope), so these hooks work
 * from anywhere in the tree regardless of where a flow was started — matching the
 * global scope of the machine registry itself.
 *
 * Additive; not wired into live flows.
 */

import { useCallback, useSyncExternalStore } from 'react';

import { subscribeToAnyMachine, subscribeToMachine } from './machineBridge';
import { isActivePhase, type RemixMetadata, type RemixPhase } from './remix-instance-types';
import { getRemixMachine, listRemixMachineIds } from './remixFlowController';

/**
 * A read-only view of a single flow, derived entirely from its machine
 * (`state()` + `context()`). This replaces the old sweet-state `RemixSnapshot`
 * mirror: every field here comes straight from the machine, the single source of
 * truth.
 */
export interface RemixInstanceView {
	id: string;
	kind: 'create' | 'update';
	phase: RemixPhase;
	conversationId: string;
	cancellationSessionId?: string;
	metadata: RemixMetadata;
	targetNodeId?: string;
	startedAt: number;
	/**
	 * Edit-only: final message id of the currently-applied result, used to
	 * suppress the already-applied result until a newer one streams in. Needed by
	 * the watcher's `deriveConversationEvent` call for edit flows.
	 */
	updateSentinel?: string;
	/** Update-only message count observed before the request was sent. */
	messageCountBeforeSend?: number;
}

const readInstanceView = (id: string): RemixInstanceView | undefined => {
	const machine = getRemixMachine(id);
	if (!machine) {
		return undefined;
	}
	const ctx = machine.context();
	return {
		id: ctx.remixId,
		kind: ctx.kind,
		phase: machine.state() as RemixPhase,
		conversationId: ctx.conversationId,
		cancellationSessionId: ctx.cancellationSessionId,
		metadata: ctx.metadata,
		targetNodeId: ctx.targetNodeId,
		startedAt: ctx.startedAt,
		updateSentinel: ctx.updateSentinel,
		messageCountBeforeSend: ctx.messageCountBeforeSend,
	};
};

/**
 * Per-id snapshot cache for `useRemixInstance`.
 *
 * `useSyncExternalStore` calls `getSnapshot` on every render and bails out of
 * re-rendering only when the returned reference is `Object.is`-equal to the
 * previous one. `readInstanceView` builds a fresh object each call, so returning
 * it directly makes React see a new reference on every render → it re-renders →
 * calls `getSnapshot` again → new reference again → infinite loop
 * ("Maximum update depth exceeded").
 *
 * We cache the last view per id and only return a NEW reference when a
 * field actually changed. Otherwise we return the cached reference, giving
 * `useSyncExternalStore` the referential stability it requires.
 */
const instanceViewCache = new Map<string, RemixInstanceView>();

const sameView = (a: RemixInstanceView | undefined, b: RemixInstanceView | undefined): boolean => {
	if (a === b) {
		return true;
	}
	if (!a || !b) {
		return false;
	}
	return (
		a.id === b.id &&
		a.kind === b.kind &&
		a.phase === b.phase &&
		a.conversationId === b.conversationId &&
		a.cancellationSessionId === b.cancellationSessionId &&
		a.targetNodeId === b.targetNodeId &&
		a.startedAt === b.startedAt &&
		a.updateSentinel === b.updateSentinel &&
		a.messageCountBeforeSend === b.messageCountBeforeSend &&
		a.metadata === b.metadata
	);
};

const getStableInstanceView = (id: string): RemixInstanceView | undefined => {
	const next = readInstanceView(id);
	if (!next) {
		instanceViewCache.delete(id);
		return undefined;
	}
	const prev = instanceViewCache.get(id);
	if (prev && sameView(prev, next)) {
		return prev;
	}
	instanceViewCache.set(id, next);
	return next;
};

/**
 * The current phase of a single flow, read live from its machine. Returns
 * `undefined` if the flow does not exist (e.g. already ended). Re-renders on
 * every transition of that machine.
 */
export const useMachinePhase = (remixId: string): RemixPhase | undefined => {
	const subscribe = useCallback(
		(onStoreChange: () => void) => subscribeToMachine(remixId, onStoreChange),
		[remixId],
	);

	const getSnapshot = useCallback((): RemixPhase | undefined => {
		const machine = getRemixMachine(remixId);
		return machine ? (machine.state() as RemixPhase) : undefined;
	}, [remixId]);

	return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
};

/**
 * Whether ANY remix flow is currently active. Reads live from the machine
 * registry; re-renders when a flow starts, ends, or any machine transitions.
 *
 * A flow counts as active while its machine is registered and not in a terminal
 * phase. This is what a "disable the remix buttons while one is in progress"
 * consumer subscribes to — and because it reads on mount too, a button that
 * mounts *after* a flow started still renders disabled.
 */

export const useIsAnyRemixing = (): boolean => {
	const getSnapshot = useCallback((): boolean => isAnyRemixingNow(), []);
	return useSyncExternalStore(subscribeToAnyMachine, getSnapshot, getSnapshot);
};

/**
 * Non-hook read of "is any flow active", for use in imperative code (e.g. a
 * guard before starting a flow, or the single-active policy). Kept next to the
 * hook so the definition of "active" lives in one place.
 */
export const isAnyRemixingNow = (): boolean => {
	for (const id of getActiveMachineIds()) {
		const machine = getRemixMachine(id);
		if (machine && isActivePhase(machine.state() as RemixPhase)) {
			return true;
		}
	}
	return false;
};

/** The ids of all currently-registered flows (from the machine registry). */
const getActiveMachineIds = (): string[] => listRemixMachineIds();

/**
 * A live, read-only view of a single flow (phase + context metadata), read
 * straight from its machine. Replaces the sweet-state `useRemixMachineInstance`
 * selector. Re-renders on that machine's transitions; returns `undefined` once
 * the flow ends.
 *
 * Note: `getSnapshot` builds a fresh object each call, so this hook re-renders
 * on every transition of the flow (acceptable — a per-flow view is expected to
 * track that flow). Consumers that only need the phase should prefer
 * `useMachinePhase`, which returns a primitive and re-renders less.
 */
export const useRemixInstance = (remixId: string): RemixInstanceView | undefined => {
	const subscribe = useCallback(
		(onStoreChange: () => void) => subscribeToMachine(remixId, onStoreChange),
		[remixId],
	);
	const getSnapshot = useCallback(
		(): RemixInstanceView | undefined => getStableInstanceView(remixId),
		[remixId],
	);
	return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
};

/**
 * The ids of all currently-active (non-terminal) flows. Replaces the sweet-state
 * `useActiveRemixMachineIds` selector. Reads live from the registry; re-renders
 * when a flow starts/ends or any machine transitions (terminal flows drop out).
 *
 * Returns a stable comma-joined string internally to give `useSyncExternalStore`
 * a referentially-stable snapshot (avoids the "getSnapshot returned a new array"
 * warning), then splits back to an array.
 */
export const useActiveRemixIds = (): string[] => {
	const getKey = useCallback((): string => activeRemixIdsNow().join(','), []);
	const key = useSyncExternalStore(subscribeToAnyMachine, getKey, getKey);
	return key === '' ? [] : key.split(',');
};

/** Non-hook read of active (non-terminal) flow ids, from the registry. */
export const activeRemixIdsNow = (): string[] =>
	getActiveMachineIds().filter((id) => {
		const machine = getRemixMachine(id);
		return machine ? isActivePhase(machine.state() as RemixPhase) : false;
	});
